import * as turf from '@turf/turf';
import { tool } from '@CesiumCommon';

/**
 * 计算两点之间的距离
 * @param {Object} ponit1 - 第一个点 { latitude, longitude }
 * @param {Object} point2 - 第二个点 { latitude, longitude }
 * @returns {string} 格式化后的距离字符串（如 "2.5km" 或 "500m"）
 */
export function getDistance (ponit1, point2) {
    let distance = +tool.distance(ponit1, point2);
    if (distance / 1000 >= 1) {
        distance = `${(distance / 1000).toFixed(1)}km`;
    } else {
        distance = `${distance}m`;
    }
    return distance;
}

/**
 * 计算多个点的中心点
 * @param {Array} points - 点数组，每个点格式为 [lng, lat]
 * @returns {Object} 中心点 { longitude, latitude }
 */
export function getPointsCenter (points) {
    if (!points || points.length === 0) return { longitude: 0, latitude: 0 };

    let lngSum = 0;
    let latSum = 0;
    points.forEach((point) => {
        lngSum += point[0];
        latSum += point[1];
    });

    return {
        longitude: lngSum / points.length,
        latitude: latSum / points.length,
    };
}

/**
 * 闭合多边形坐标环
 * @param {Array} coords - 坐标数组
 * @returns {Array} 闭合后的坐标数组
 */
export function closePolygonRing (coords) {
    if (!Array.isArray(coords) || coords.length === 0) {
        return [[[0, 0], [0, 1], [1, 1], [1, 0], [0, 0]]];
    }

    let ring = coords[0] || coords;
    if (!Array.isArray(ring)) return [[[0, 0], [0, 1], [1, 1], [1, 0], [0, 0]]];

    if (ring.length < 3) {
        return [[[0, 0], [0, 1], [1, 1], [1, 0], [0, 0]]];
    }

    const first = ring[0];
    const last = ring[ring.length - 1];
    if (first[0] !== last[0] || first[1] !== last[1]) {
        ring = [...ring, first];
    }
    return [ring];
}

/**
 * 判断点是否在多边形高度限制区内，收集符合条件的高度值
 * @param {Array} arr - 点坐标数组 [lng, lat][]
 * @param {Array} poylonList - 多边形列表 [{coordinates, height}, ...]
 * @returns {Array} 符合条件的高度值数组
 */
export function heightJudge (arr, poylonList) {
    let flagArr = [];
    poylonList.forEach((poylon) => {
        try {
            if (!poylon.coordinates || poylon.coordinates.length === 0) {
                console.warn('无效的多边形坐标:', poylon);
                return;
            }

            let closedPoylonCoords = closePolygonRing([poylon.coordinates]);
            let poly2 = turf.polygon(closedPoylonCoords);

            let point = turf.point(arr);
            let status = turf.booleanOverlap(point, poly2);
            if (status) {
                flagArr.push(Number(poylon.height || 0));
            }
        } catch (e) {
            console.error('多边形构造失败:', e, poylon);
        }
    });
    return flagArr;
}

/**
 * 计算航线总距离
 * @param {Array} points - 航线点数组 [{latitude, longitude}, ...]
 * @returns {string} 总距离（公里，保留两位小数）
 */
export function getRouteDistance (points, Utils) {
    let sum = 0;
    if (!Array.isArray(points) || points.length < 2) return '0.00';

    points.forEach((item, i) => {
        if (i > 0) {
            let point1 = points[i - 1];
            let point2 = points[i];
            let p1 = { lat: point1.latitude, lng: point1.longitude };
            let p2 = { lat: point2.latitude, lng: point2.longitude };
            let d = Utils.getDistance(p1.lat, p1.lng, p2.lng, p2.lng);
            sum += d;
        }
    });
    return (sum / 1000).toFixed(2);
}

/**
 * 计算预计飞行时间（平均速度 15m/s）
 * @param {Array} points - 航线点数组 [{latitude, longitude}, ...]
 * @param {number} speed - 飞行速度，单位 m/s，默认 15
 * @returns {string} 预计时间（分钟，保留两位小数）
 */
export function getEstimateTime (points, speed = 15) {
    let second = 0;
    if (!Array.isArray(points) || points.length < 2) return '0.00';

    points.forEach((item, i) => {
        if (i > 0) {
            let point1 = points[i - 1];
            let point2 = points[i];
            let p1 = { lat: Number(point1.latitude), lng: Number(point1.longitude) };
            let p2 = { lat: Number(point2.latitude), lng: Number(point2.longitude) };
            let d = tool.distance(p1, p2);
            second += d / Number(speed);
        }
    });
    return (Number(second) / 60).toFixed(2);
}
